/*
 * SUPERADMIN SETUP CHECKLIST
 * Run these SQL statements in Supabase SQL editor before first use:
 *
 * -- 1. Extend role constraint to allow 'superadmin'
 * ALTER TABLE users DROP CONSTRAINT users_role_check;
 * ALTER TABLE users ADD CONSTRAINT users_role_check
 *   CHECK (role IN ('customer', 'company', 'vendor', 'admin', 'superadmin'));
 *
 * -- 2. Grant superadmin role to operator account
 * UPDATE users SET role = 'superadmin' WHERE email = 'cscdav@gmail.com';
 *
 * Then log in as cscdav@gmail.com to access /superadmin.
 */

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Navbar, useAuth } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { useToast } from "../hooks/use-toast";
import { Copy, ExternalLink, Mail, RefreshCw } from "lucide-react";
import {
  saGetStats, saGetVendors, saCreateVendor, saUpdateVendor, saToggleVendor,
  saGetCustomers, saDeactivateCustomer,
  saGetApplications, saUpdateApplicationStatus,
  saGetOrders,
} from "../lib/api";

const SUPERADMIN_EMAIL = 'cscdav@gmail.com';

function slugify(s) {
  return 'v-' + s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function fmt(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString();
}

function copyText(text, toast) {
  navigator.clipboard.writeText(text);
  toast({ title: "Copied!" });
}

// ── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, highlight }) {
  return (
    <Card className={highlight ? "border-amber-300 bg-amber-50" : ""}>
      <CardContent className="p-4 text-center">
        <div className="text-3xl font-bold">{value ?? '—'}</div>
        <div className="text-xs text-neutral-500 mt-1">{label}</div>
      </CardContent>
    </Card>
  );
}

// ── Vendors Tab ───────────────────────────────────────────────────────────────
function VendorsTab({ toast }) {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showCreated, setShowCreated] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [createdResult, setCreatedResult] = useState(null);

  const [addForm, setAddForm] = useState({ business_name: '', contact_name: '', email: '', vendor_id: '' });
  const [editForm, setEditForm] = useState({ name: '', description: '', image: '', hours: '', pickup_address: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    saGetVendors().then(setVendors).catch(() => toast({ title: "Error loading vendors" })).finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  function openEdit(v) {
    setEditTarget(v);
    setEditForm({ name: v.name || '', description: v.description || '', image: v.image || '', hours: v.hours || '', pickup_address: v.pickup_address || '' });
    setShowEdit(true);
  }

  async function handleAdd(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...addForm, vendor_id: addForm.vendor_id || slugify(addForm.business_name) };
      const result = await saCreateVendor(payload);
      setCreatedResult(result);
      setShowAdd(false);
      setAddForm({ business_name: '', contact_name: '', email: '', vendor_id: '' });
      setShowCreated(true);
      load();
    } catch (err) {
      toast({ title: "Error", description: err?.response?.data?.detail || "Could not create vendor" });
    } finally {
      setSaving(false);
    }
  }

  async function handleEdit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await saUpdateVendor(editTarget.id, editForm);
      toast({ title: "Vendor updated" });
      setShowEdit(false);
      load();
    } catch (err) {
      toast({ title: "Error", description: err?.response?.data?.detail || "Could not update vendor" });
    } finally {
      setSaving(false);
    }
  }

  async function handleToggle(v) {
    try {
      await saToggleVendor(v.id);
      toast({ title: `Vendor ${v.is_active ? 'deactivated' : 'activated'}` });
      load();
    } catch {
      toast({ title: "Error toggling vendor" });
    }
  }

  const inviteUrl = (v) => `${window.location.origin}/join/${v.invite_token || v.id}`;

  return (
    <div className="space-y-4 mt-4">
      <div className="flex justify-between items-center">
        <span className="text-sm text-neutral-500">{vendors.length} vendor{vendors.length !== 1 ? 's' : ''}</span>
        <Button onClick={() => setShowAdd(true)} className="bg-black text-white hover:bg-neutral-800">+ Add vendor</Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Pilot</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5}><div className="animate-pulse space-y-2 py-2">{[1,2,3].map(i => <div key={i} className="h-3 bg-neutral-200 rounded w-full" />)}</div></TableCell></TableRow>
            ) : vendors.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center text-sm text-neutral-500 py-8">No vendors</TableCell></TableRow>
            ) : vendors.map(v => (
              <TableRow key={v.id}>
                <TableCell className="font-mono text-xs">{v.id}</TableCell>
                <TableCell className="font-medium">{v.name}</TableCell>
                <TableCell>
                  <Badge className={v.is_active ? "bg-green-100 text-green-800 border-green-200" : "bg-neutral-100 text-neutral-500"}>
                    {v.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs">{v.is_pilot_vendor ? 'Yes' : '—'}</TableCell>
                <TableCell>
                  <div className="flex gap-2 flex-wrap">
                    <Link to={`/company/${v.id}`} target="_blank"><Button variant="outline" size="sm">View <ExternalLink className="w-3 h-3 ml-1" /></Button></Link>
                    <Button variant="outline" size="sm" onClick={() => openEdit(v)}>Edit</Button>
                    <Button variant="outline" size="sm" onClick={() => handleToggle(v)}>
                      {v.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => copyText(inviteUrl(v), toast)}>
                      <Copy className="w-3 h-3 mr-1" /> Invite link
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Add Vendor Modal */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add vendor</DialogTitle></DialogHeader>
          <form onSubmit={handleAdd} className="space-y-3 mt-2">
            <div>
              <label className="text-sm font-medium block mb-1">Business name <span className="text-red-500">*</span></label>
              <Input required value={addForm.business_name} onChange={e => setAddForm(f => ({ ...f, business_name: e.target.value, vendor_id: slugify(e.target.value) }))} className="bg-white" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Vendor ID (auto-generated, editable)</label>
              <Input value={addForm.vendor_id} onChange={e => setAddForm(f => ({ ...f, vendor_id: e.target.value }))} className="bg-white font-mono text-sm" placeholder="v-business-name" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Contact name <span className="text-red-500">*</span></label>
              <Input required value={addForm.contact_name} onChange={e => setAddForm(f => ({ ...f, contact_name: e.target.value }))} className="bg-white" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Email <span className="text-red-500">*</span></label>
              <Input required type="email" value={addForm.email} onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))} className="bg-white" />
            </div>
            <p className="text-xs text-neutral-500">A temporary password will be auto-generated and shown after creation.</p>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-black text-white hover:bg-neutral-800">{saving ? 'Creating…' : 'Create vendor'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Vendor Modal */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit vendor — {editTarget?.id}</DialogTitle></DialogHeader>
          <form onSubmit={handleEdit} className="space-y-3 mt-2">
            <div>
              <label className="text-sm font-medium block mb-1">Business name</label>
              <Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} className="bg-white" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Description</label>
              <Textarea value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} className="bg-white" rows={2} />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Photo URL</label>
              <Input value={editForm.image} onChange={e => setEditForm(f => ({ ...f, image: e.target.value }))} className="bg-white" placeholder="https://…" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Hours</label>
              <Input value={editForm.hours} onChange={e => setEditForm(f => ({ ...f, hours: e.target.value }))} className="bg-white" placeholder="Mon–Fri 9am–6pm" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Pickup address</label>
              <Input value={editForm.pickup_address} onChange={e => setEditForm(f => ({ ...f, pickup_address: e.target.value }))} className="bg-white" placeholder="123 Main St, Austin TX" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowEdit(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-black text-white hover:bg-neutral-800">{saving ? 'Saving…' : 'Save'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Created Vendor Credentials Modal */}
      <Dialog open={showCreated} onOpenChange={setShowCreated}>
        <DialogContent>
          <DialogHeader><DialogTitle>Vendor created</DialogTitle></DialogHeader>
          {createdResult && (
            <div className="space-y-3 mt-2 text-sm">
              <p className="text-neutral-600">Share these credentials with the vendor. The password cannot be recovered after closing this dialog.</p>
              <div className="space-y-2 bg-neutral-50 rounded-lg p-4 border">
                <div className="flex justify-between items-center">
                  <span className="text-neutral-500">Vendor ID</span>
                  <div className="flex items-center gap-2">
                    <code className="font-mono">{createdResult.vendor_id}</code>
                    <Button variant="ghost" size="sm" onClick={() => copyText(createdResult.vendor_id, toast)}><Copy className="w-3 h-3" /></Button>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-neutral-500">Email</span>
                  <code className="font-mono">{createdResult.email}</code>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-neutral-500">Password</span>
                  <div className="flex items-center gap-2">
                    <code className="font-mono">{createdResult.password}</code>
                    <Button variant="ghost" size="sm" onClick={() => copyText(createdResult.password, toast)}><Copy className="w-3 h-3" /></Button>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-neutral-500">Invite link</span>
                  <div className="flex items-center gap-2">
                    <code className="font-mono text-xs">/join/{createdResult.invite_token}</code>
                    <Button variant="ghost" size="sm" onClick={() => copyText(`${window.location.origin}/join/${createdResult.invite_token}`, toast)}><Copy className="w-3 h-3" /></Button>
                  </div>
                </div>
              </div>
              <div className="flex justify-end">
                <Button onClick={() => setShowCreated(false)} className="bg-black text-white hover:bg-neutral-800">Done</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Customers Tab ─────────────────────────────────────────────────────────────
function CustomersTab({ toast }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    saGetCustomers().then(setCustomers).catch(() => toast({ title: "Error loading customers" })).finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  async function handleDeactivate(c) {
    if (!window.confirm(`Deactivate account for ${c.email}?`)) return;
    try {
      await saDeactivateCustomer(c.id);
      toast({ title: "Account deactivated" });
      load();
    } catch (err) {
      toast({ title: "Error", description: err?.response?.data?.detail || "Could not deactivate" });
    }
  }

  return (
    <div className="space-y-4 mt-4">
      <span className="text-sm text-neutral-500">{customers.length} customer{customers.length !== 1 ? 's' : ''}</span>
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Vendor bound to</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5}><div className="animate-pulse space-y-2 py-2">{[1,2,3].map(i => <div key={i} className="h-3 bg-neutral-200 rounded w-full" />)}</div></TableCell></TableRow>
            ) : customers.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center text-sm text-neutral-500 py-8">No customers yet</TableCell></TableRow>
            ) : customers.map(c => (
              <TableRow key={c.id}>
                <TableCell className="font-medium">{c.name || '—'}</TableCell>
                <TableCell className="text-sm">{c.email}</TableCell>
                <TableCell className="font-mono text-xs">{c.vendor_id || '—'}</TableCell>
                <TableCell className="text-sm">{fmt(c.created_at)}</TableCell>
                <TableCell>
                  <Button variant="outline" size="sm" onClick={() => handleDeactivate(c)} className="text-red-600 border-red-200 hover:bg-red-50">
                    Deactivate
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ── Applications Tab ──────────────────────────────────────────────────────────
function ApplicationsTab({ toast }) {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [showApprove, setShowApprove] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveForm, setApproveForm] = useState({ business_name: '', contact_name: '', email: '', vendor_id: '' });
  const [saving, setSaving] = useState(false);
  const [createdResult, setCreatedResult] = useState(null);
  const [showCreated, setShowCreated] = useState(false);
  const { saCreateVendor: _createVendor } = { saCreateVendor };

  const load = useCallback(() => {
    setLoading(true);
    saGetApplications().then(setApps).catch(() => toast({ title: "Error loading applications" })).finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const filtered = apps.filter(a => filter === 'all' ? true : a.status === filter);
  const pendingCount = apps.filter(a => a.status === 'pending').length;

  async function handleStatus(id, status) {
    try {
      await saUpdateApplicationStatus(id, status);
      toast({ title: `Application ${status}` });
      load();
    } catch (err) {
      toast({ title: "Error", description: err?.response?.data?.detail || "Could not update" });
    }
  }

  function openApprove(app) {
    setApproveTarget(app);
    setApproveForm({
      business_name: app.business_name || '',
      contact_name: app.name || '',
      email: app.email || '',
      vendor_id: slugify(app.business_name || ''),
    });
    setShowApprove(true);
  }

  async function handleApprove(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const result = await saCreateVendor(approveForm);
      await saUpdateApplicationStatus(approveTarget.id, 'approved');
      setCreatedResult(result);
      setShowApprove(false);
      setShowCreated(true);
      load();
    } catch (err) {
      toast({ title: "Error", description: err?.response?.data?.detail || "Could not create vendor" });
    } finally {
      setSaving(false);
    }
  }

  const licenseLabel = { yes: 'Yes', no: 'No', in_progress: 'In progress' };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex items-center gap-3">
        <div className="flex gap-1">
          {['pending', 'approved', 'rejected', 'all'].map(s => (
            <Button key={s} size="sm" variant={filter === s ? 'default' : 'outline'} onClick={() => setFilter(s)}
              className={filter === s ? 'bg-black text-white' : ''}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
              {s === 'pending' && pendingCount > 0 && (
                <Badge className="ml-1.5 bg-amber-500 text-white text-[10px] px-1.5">{pendingCount}</Badge>
              )}
            </Button>
          ))}
        </div>
        <Button variant="ghost" size="sm" onClick={load}><RefreshCw className="w-3 h-3" /></Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Business</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>City</TableHead>
              <TableHead>Meals/wk</TableHead>
              <TableHead>License</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9}><div className="animate-pulse space-y-2 py-2">{[1,2,3].map(i => <div key={i} className="h-3 bg-neutral-200 rounded w-full" />)}</div></TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center text-sm text-neutral-500 py-8">No applications</TableCell></TableRow>
            ) : filtered.map(a => (
              <TableRow key={a.id}>
                <TableCell className="font-medium">{a.business_name}</TableCell>
                <TableCell>{a.name}</TableCell>
                <TableCell className="text-sm">{a.email}</TableCell>
                <TableCell className="text-sm">{a.city_state}</TableCell>
                <TableCell className="text-sm">{a.meals_per_week}</TableCell>
                <TableCell className="text-sm">{licenseLabel[a.has_license] || a.has_license}</TableCell>
                <TableCell className="text-sm">{fmt(a.created_at)}</TableCell>
                <TableCell>
                  <Badge className={
                    a.status === 'pending' ? 'bg-amber-100 text-amber-800 border-amber-200' :
                    a.status === 'approved' ? 'bg-green-100 text-green-800 border-green-200' :
                    'bg-red-100 text-red-800 border-red-200'
                  }>{a.status}</Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1.5">
                    {a.status === 'pending' && (
                      <>
                        <Button size="sm" onClick={() => openApprove(a)} className="bg-green-700 hover:bg-green-800 text-white">Approve</Button>
                        <Button size="sm" variant="outline" onClick={() => handleStatus(a.id, 'rejected')} className="text-red-600 border-red-200 hover:bg-red-50">Reject</Button>
                      </>
                    )}
                    {a.status === 'approved' && (
                      <Button size="sm" variant="outline" onClick={() => handleStatus(a.id, 'pending')}>Reopen</Button>
                    )}
                    {a.status === 'rejected' && (
                      <Button size="sm" variant="outline" onClick={() => handleStatus(a.id, 'pending')}>Reconsider</Button>
                    )}
                    <a href={`mailto:${a.email}`} target="_blank" rel="noopener noreferrer">
                      <Button size="sm" variant="outline"><Mail className="w-3 h-3" /></Button>
                    </a>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Approve → Create Vendor Modal */}
      <Dialog open={showApprove} onOpenChange={setShowApprove}>
        <DialogContent>
          <DialogHeader><DialogTitle>Approve &amp; create vendor account</DialogTitle></DialogHeader>
          <form onSubmit={handleApprove} className="space-y-3 mt-2">
            <div>
              <label className="text-sm font-medium block mb-1">Business name</label>
              <Input required value={approveForm.business_name} onChange={e => setApproveForm(f => ({ ...f, business_name: e.target.value }))} className="bg-white" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Vendor ID</label>
              <Input value={approveForm.vendor_id} onChange={e => setApproveForm(f => ({ ...f, vendor_id: e.target.value }))} className="bg-white font-mono text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Contact name</label>
              <Input required value={approveForm.contact_name} onChange={e => setApproveForm(f => ({ ...f, contact_name: e.target.value }))} className="bg-white" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Email</label>
              <Input required type="email" value={approveForm.email} onChange={e => setApproveForm(f => ({ ...f, email: e.target.value }))} className="bg-white" />
            </div>
            <p className="text-xs text-neutral-500">A temporary password will be auto-generated. Application status will be set to Approved.</p>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowApprove(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-black text-white hover:bg-neutral-800">{saving ? 'Creating…' : 'Create vendor account'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Created Credentials Modal */}
      <Dialog open={showCreated} onOpenChange={setShowCreated}>
        <DialogContent>
          <DialogHeader><DialogTitle>Vendor account created</DialogTitle></DialogHeader>
          {createdResult && (
            <div className="space-y-3 mt-2 text-sm">
              <p className="text-neutral-600">Share these credentials. The password cannot be recovered after closing.</p>
              <div className="space-y-2 bg-neutral-50 rounded-lg p-4 border">
                {[
                  { label: 'Vendor ID', val: createdResult.vendor_id },
                  { label: 'Email', val: createdResult.email },
                  { label: 'Password', val: createdResult.password },
                ].map(({ label, val }) => (
                  <div key={label} className="flex justify-between items-center">
                    <span className="text-neutral-500">{label}</span>
                    <div className="flex items-center gap-2">
                      <code className="font-mono">{val}</code>
                      <Button variant="ghost" size="sm" onClick={() => copyText(val, toast)}><Copy className="w-3 h-3" /></Button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-end">
                <Button onClick={() => setShowCreated(false)} className="bg-black text-white hover:bg-neutral-800">Done</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Orders Tab ────────────────────────────────────────────────────────────────
function OrdersTab({ toast }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vendorFilter, setVendorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    setLoading(true);
    saGetOrders().then(setOrders).catch(() => toast({ title: "Error loading orders" })).finally(() => setLoading(false));
  }, [toast]);

  const vendorIds = [...new Set(orders.map(o => o.company_id).filter(Boolean))];
  const statuses = [...new Set(orders.map(o => o.status).filter(Boolean))];

  const filtered = orders.filter(o =>
    (!vendorFilter || o.company_id === vendorFilter) &&
    (!statusFilter || o.status === statusFilter)
  );

  return (
    <div className="space-y-4 mt-4">
      <div className="flex gap-3 flex-wrap items-center">
        <select value={vendorFilter} onChange={e => setVendorFilter(e.target.value)} className="border rounded-md p-1.5 text-sm bg-white">
          <option value="">All vendors</option>
          {vendorIds.map(id => <option key={id} value={id}>{id}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border rounded-md p-1.5 text-sm bg-white">
          <option value="">All statuses</option>
          {statuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <span className="text-sm text-neutral-500">{filtered.length} order{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order ID</TableHead>
              <TableHead>Vendor</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6}><div className="animate-pulse space-y-2 py-2">{[1,2,3].map(i => <div key={i} className="h-3 bg-neutral-200 rounded w-full" />)}</div></TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-sm text-neutral-500 py-8">No orders</TableCell></TableRow>
            ) : filtered.map(o => (
              <TableRow key={o.id}>
                <TableCell className="font-mono text-xs">{(o.id || '').slice(0, 8)}…</TableCell>
                <TableCell className="font-mono text-xs">{o.company_id || '—'}</TableCell>
                <TableCell className="font-mono text-xs">{(o.user_id || '—').slice(0, 8)}{o.user_id ? '…' : ''}</TableCell>
                <TableCell className="text-sm">{o.total_amount != null ? `$${Number(o.total_amount).toFixed(2)}` : '—'}</TableCell>
                <TableCell><Badge className="text-xs">{o.status || '—'}</Badge></TableCell>
                <TableCell className="text-sm">{fmt(o.created_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ── Platform Tab ──────────────────────────────────────────────────────────────
function PlatformTab({ toast }) {
  const [stats, setStats] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [selectedVendorId, setSelectedVendorId] = useState('');

  useEffect(() => {
    saGetStats().then(setStats).catch(() => {});
    saGetVendors().then(setVendors).catch(() => {});
  }, []);

  const selectedVendor = vendors.find(v => v.id === selectedVendorId);
  const inviteUrl = selectedVendor ? `${window.location.origin}/join/${selectedVendor.invite_token || selectedVendor.id}` : '';

  const pilotMode = process.env.REACT_APP_PILOT_MODE === 'true';

  const legalLinks = [
    { label: 'Terms of Service', path: '/terms' },
    { label: 'Privacy Policy', path: '/privacy' },
    { label: 'Marketplace Disclaimer', path: '/disclaimer' },
    { label: 'Vendor Agreement', path: '/vendor-agreement' },
    { label: 'Refund Policy', path: '/refund' },
  ];

  return (
    <div className="space-y-8 mt-4">
      {/* Stats */}
      <div>
        <h3 className="text-sm font-semibold text-neutral-700 mb-3">Platform stats</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label="Vendors" value={stats?.vendors} />
          <StatCard label="Customers" value={stats?.customers} />
          <StatCard label="Orders" value={stats?.orders} />
          <StatCard label="Applications" value={stats?.applications} />
          <StatCard label="Pending applications" value={stats?.pending_applications} highlight={stats?.pending_applications > 0} />
        </div>
      </div>

      {/* Pilot mode info */}
      <div>
        <h3 className="text-sm font-semibold text-neutral-700 mb-3">Pilot mode</h3>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <Badge className={pilotMode ? "bg-green-100 text-green-800 border-green-200" : "bg-neutral-100 text-neutral-500"}>
              {pilotMode ? 'ON' : 'OFF'}
            </Badge>
            <p className="text-sm text-neutral-600">
              Pilot mode is a <strong>build-time</strong> environment variable (<code className="text-xs bg-neutral-100 px-1 rounded">REACT_APP_PILOT_MODE</code>).
              To change it, update the value in Vercel dashboard and trigger a redeploy.
              Backend pilot mode is controlled by <code className="text-xs bg-neutral-100 px-1 rounded">PILOT_MODE</code> in Render.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Invite link generator */}
      <div>
        <h3 className="text-sm font-semibold text-neutral-700 mb-3">Invite link generator</h3>
        <Card>
          <CardContent className="p-4 space-y-3">
            <select value={selectedVendorId} onChange={e => setSelectedVendorId(e.target.value)} className="w-full border rounded-md p-2 text-sm bg-white">
              <option value="">Select a vendor…</option>
              {vendors.map(v => <option key={v.id} value={v.id}>{v.name} ({v.id})</option>)}
            </select>
            {inviteUrl && (
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-neutral-50 border rounded px-3 py-2 break-all">{inviteUrl}</code>
                <Button variant="outline" size="sm" onClick={() => copyText(inviteUrl, toast)}><Copy className="w-3 h-3 mr-1" /> Copy</Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick links */}
      <div>
        <h3 className="text-sm font-semibold text-neutral-700 mb-3">Legal pages</h3>
        <div className="flex flex-wrap gap-2">
          {legalLinks.map(l => (
            <Link key={l.path} to={l.path} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm">{l.label} <ExternalLink className="w-3 h-3 ml-1" /></Button>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main SuperAdmin Component ─────────────────────────────────────────────────
export default function SuperAdmin() {
  const { user, setUser, setToken } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Auth guard — both checks must pass, redirect silently on failure
  useEffect(() => {
    if (user === undefined) return; // still loading
    if (!user || user.role !== 'superadmin' || user.email !== SUPERADMIN_EMAIL) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  if (!user || user.role !== 'superadmin' || user.email !== SUPERADMIN_EMAIL) {
    return null;
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">PrepMate Operator Dashboard</h1>
          <p className="text-sm text-neutral-500 mt-1">Vizcaya Labs — internal use only</p>
        </div>

        <Tabs defaultValue="vendors">
          <TabsList className="mb-4">
            <TabsTrigger value="vendors">Vendors</TabsTrigger>
            <TabsTrigger value="customers">Customers</TabsTrigger>
            <TabsTrigger value="applications">Applications</TabsTrigger>
            <TabsTrigger value="orders">Orders</TabsTrigger>
            <TabsTrigger value="platform">Platform</TabsTrigger>
          </TabsList>

          <TabsContent value="vendors"><VendorsTab toast={toast} /></TabsContent>
          <TabsContent value="customers"><CustomersTab toast={toast} /></TabsContent>
          <TabsContent value="applications"><ApplicationsTab toast={toast} /></TabsContent>
          <TabsContent value="orders"><OrdersTab toast={toast} /></TabsContent>
          <TabsContent value="platform"><PlatformTab toast={toast} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
